import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Flame, Plus, Search, Globe, FileText, Zap, TrendingUp, 
  Settings, BarChart3, Download, Play, Pause, Trash2, Eye,
  RefreshCw, CheckCircle, Clock, AlertCircle, Layers, Target,
  ArrowRight, ArrowLeft, Link2, Scan, Rocket, Map, Tag, 
  Building2, ExternalLink, Copy, Check, ChevronRight, Sparkles, MapPin,
  Phone, Palette
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Use relative API URL for production (when on seofurnace.com.au domain)
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname.includes('seofurnace') || hostname.includes('puppoto') || hostname.includes('46.250.241.180')) {
    return ''; // Use relative URL - same domain
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API_URL = getApiUrl();

// Campaign status colors
const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  paused: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  generating: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  deployed: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

// Wizard Steps
const WIZARD_STEPS = [
  { id: 1, title: 'Domain Setup', icon: Globe, description: 'Configure your SEO domain' },
  { id: 2, title: 'Destination', icon: Link2, description: 'Where should traffic go?' },
  { id: 3, title: 'Mirror Branding', icon: Scan, description: 'Extract & match branding' },
  { id: 4, title: 'Targeting', icon: Target, description: 'Location & keyword targeting' },
  { id: 5, title: 'Products/Services', icon: Tag, description: 'What are you promoting?' },
  { id: 6, title: 'Launch', icon: Rocket, description: 'Review & deploy campaign' },
];

const SEOFurnace = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showLeads, setShowLeads] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showCampaignDetail, setShowCampaignDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  
  // Campaign wizard data
  const [campaignData, setCampaignData] = useState({
    // Step 1: Domain
    name: '',
    domain: '',
    hasDomain: 'yes',
    
    // Step 2: Destination
    destinationUrl: '',
    destinationType: 'external', // 'external' or 'internal'
    
    // Step 3: Scanned branding data
    businessName: '',
    businessDescription: '',
    brandColors: { primary: '#00B0B0', secondary: '#008A8A' },
    logoUrl: '',
    phone: '',
    heroTitle: '',
    heroSubtitle: '',
    navItems: [],
    
    // Step 4: Targeting
    targetingScope: 'local', // 'local', 'state', 'national'
    locations: '',
    
    // Step 5: Products/Services
    keywords: '',
    services: '',
    
    // Step 6: Settings
    pagesToGenerate: 100,
    includeAiContent: true,
    generateSitemap: true,
    generateLegalPages: true,
  });

  // Stats
  const [stats, setStats] = useState({
    total_campaigns: 0,
    total_pages: 0,
    indexed_pages: 0,
    total_leads: 0,
  });

  const handleLogin = () => {
    if (password === 'furnace2024') {
      setIsAuthenticated(true);
      toast.success('Welcome to SEO Furnace 🔥');
      fetchCampaigns();
      fetchStats();
    } else {
      toast.error('Invalid password');
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/furnace/campaigns`);
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/furnace/stats`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchLeads = async (campaignId = null) => {
    try {
      // Always fetch all leads to ensure they show
      const url = `${API_URL}/api/furnace/leads`;
      const res = await fetch(url);
      const data = await res.json();
      let leadsData = data.leads || [];
      
      // Filter client-side if campaign specified
      if (campaignId && leadsData.length > 0) {
        leadsData = leadsData.filter(l => l.campaign_id === campaignId);
      }
      
      setLeads(leadsData);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      setLeads([]);
    }
  };

  const viewLeads = (campaign) => {
    setSelectedCampaign(campaign);
    // Fetch all leads first, then show modal
    fetchLeads(campaign?.id);
    setShowLeads(true);
  };

  const exportLeadsCSV = () => {
    if (leads.length === 0) return;
    
    const headers = ['Name', 'Email', 'Phone', 'Message', 'Source Page', 'Status', 'Date'];
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        `"${lead.name}"`,
        lead.email,
        lead.phone,
        `"${lead.message?.replace(/"/g, '""') || ''}"`,
        lead.source_page,
        lead.status,
        new Date(lead.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${selectedCampaign?.name || 'all'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Leads exported to CSV!');
  };

  // Deploy campaign - turnkey solution
  const deployCampaign = async (campaignId) => {
    setDeploying(true);
    setDeployResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/furnace/campaigns/${campaignId}/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      const data = await res.json();
      
      if (data.success) {
        setDeployResult(data);
        toast.success(`🚀 Deployed! ${data.pages_created} pages live at ${data.site_url}`);
        fetchCampaigns();
      } else {
        toast.error(data.detail || 'Deployment failed');
      }
    } catch (error) {
      toast.error('Deployment failed: ' + error.message);
    } finally {
      setDeploying(false);
    }
  };

  // View campaign details
  const viewCampaignDetails = (campaign) => {
    setSelectedCampaign(campaign);
    setShowCampaignDetail(true);
  };

  // Scan destination website for branding
  const scanWebsite = async () => {
    if (!campaignData.destinationUrl) {
      toast.error('Please enter a destination URL');
      return;
    }
    
    setScanning(true);
    try {
      // Call the actual scan API
      const res = await fetch(`${API_URL}/api/furnace/scan-website`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: campaignData.destinationUrl }),
      });
      
      const data = await res.json();
      
      if (data.success || data.brand_name || data.primary_color) {
        setScannedData({
          businessName: data.brand_name || '',
          description: data.meta_description || data.hero_subtitle || '',
          phone: data.phone || '',
          email: data.email || '',
          address: data.address || '',
          logoUrl: data.logo_url || '',
          heroTitle: data.hero_title || '',
          heroSubtitle: data.hero_subtitle || '',
          primaryColor: data.primary_color || '#00B0B0',
          secondaryColor: data.secondary_color || '#008A8A',
          navItems: data.nav_items || [],
          services: data.services || [],
          socialLinks: data.social_links || {},
        });
        
        setCampaignData(prev => ({
          ...prev,
          businessName: data.brand_name || prev.businessName,
          businessDescription: data.meta_description || data.hero_subtitle || prev.businessDescription,
          phone: data.phone || '',
          logoUrl: data.logo_url || '',
          heroTitle: data.hero_title || '',
          heroSubtitle: data.hero_subtitle || '',
          brandColors: { 
            primary: data.primary_color || '#00B0B0', 
            secondary: data.secondary_color || '#008A8A' 
          },
          navItems: data.nav_items || [],
        }));
        
        toast.success('Website scanned! Branding extracted successfully.');
      } else {
        // Fallback: Extract from URL
        const url = new URL(campaignData.destinationUrl.startsWith('http') ? campaignData.destinationUrl : `https://${campaignData.destinationUrl}`);
        const domainParts = url.hostname.replace('www.', '').split('.');
        const suggestedName = domainParts[0].charAt(0).toUpperCase() + domainParts[0].slice(1);
        
        setScannedData({
          businessName: suggestedName,
          description: `Professional services provided by ${suggestedName}`,
          primaryColor: '#00B0B0',
          secondaryColor: '#008A8A',
        });
        
        setCampaignData(prev => ({
          ...prev,
          businessName: suggestedName,
          businessDescription: `Professional services provided by ${suggestedName}`,
        }));
        
        toast.info('Basic info extracted. Add more details manually.');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to scan website. Please enter details manually.');
    } finally {
      setScanning(false);
    }
  };

  const createCampaign = async () => {
    setLoading(true);
    try {
      const keywordsArray = campaignData.keywords.split('\n').filter(k => k.trim());
      const locationsArray = campaignData.locations.split('\n').filter(l => l.trim());
      const servicesArray = campaignData.services.split('\n').filter(s => s.trim());
      
      // Create the campaign
      const res = await fetch(`${API_URL}/api/furnace/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: campaignData.name,
          domain: campaignData.domain,
          destination_url: campaignData.destinationUrl,
          business_name: campaignData.businessName,
          business_description: campaignData.businessDescription,
          targeting_scope: campaignData.targetingScope,
          keywords: keywordsArray,
          locations: locationsArray,
          services: servicesArray,
          pages_to_generate: campaignData.pagesToGenerate,
          include_ai_snippets: campaignData.includeAiContent,
          generate_sitemap: campaignData.generateSitemap,
          generate_about: campaignData.generateLegalPages,
          generate_contact: campaignData.generateLegalPages,
          generate_privacy: campaignData.generateLegalPages,
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        const campaignId = data.campaign_id;
        
        // Apply the branding data to the campaign
        if (campaignId && (scannedData || campaignData.brandColors?.primary)) {
          await fetch(`${API_URL}/api/furnace/campaigns/${campaignId}/apply-branding`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              brand_name: campaignData.businessName,
              logo_url: campaignData.logoUrl || '',
              primary_color: campaignData.brandColors?.primary || '#00B0B0',
              secondary_color: campaignData.brandColors?.secondary || '#008A8A',
              phone: campaignData.phone || '',
              hero_title: campaignData.heroTitle || '',
              hero_subtitle: campaignData.heroSubtitle || campaignData.businessDescription || '',
              nav_items: campaignData.navItems || [],
              meta_description: campaignData.businessDescription || '',
            }),
          });
        }
        
        toast.success('Campaign created with branding! 🔥 Ready to generate mirror pages.');
        setShowWizard(false);
        resetWizard();
        fetchCampaigns();
        fetchStats();
      } else {
        toast.error('Failed to create campaign');
      }
    } catch (error) {
      toast.error('Error creating campaign');
    } finally {
      setLoading(false);
    }
  };

  const startGeneration = async (campaignId) => {
    try {
      toast.loading('Starting page generation...', { id: 'generate' });
      const res = await fetch(`${API_URL}/api/furnace/campaigns/${campaignId}/generate`, {
        method: 'POST',
      });
      if (res.ok) {
        toast.success('Page generation started! 🔥', { id: 'generate' });
        fetchCampaigns();
      } else {
        toast.error('Failed to start generation', { id: 'generate' });
      }
    } catch (error) {
      toast.error('Failed to start generation', { id: 'generate' });
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setCampaignData({
      name: '',
      domain: '',
      hasDomain: 'yes',
      destinationUrl: '',
      destinationType: 'external',
      businessName: '',
      businessDescription: '',
      brandColors: { primary: '#0ea5e9', secondary: '#f97316' },
      logo: '',
      targetingScope: 'local',
      locations: '',
      keywords: '',
      services: '',
      pagesToGenerate: 100,
      includeAiContent: true,
      generateSitemap: true,
      generateLegalPages: true,
    });
    setScannedData(null);
  };

  const nextStep = () => {
    if (wizardStep < 6) setWizardStep(wizardStep + 1);
  };

  const prevStep = () => {
    if (wizardStep > 1) setWizardStep(wizardStep - 1);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-center justify-center p-4">
        <Helmet>
          <title>SEO Furnace | Login</title>
        </Helmet>
        
        {/* Animated background flames */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 w-full max-w-md border border-orange-500/20 shadow-2xl shadow-orange-500/10">
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-orange-500/50 animate-pulse">
              <Flame className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 mb-2">
              SEO FURNACE
            </h1>
            <p className="text-slate-400">Incinerate the Competition 🔥</p>
          </div>
          
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Enter access code"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className="bg-slate-900/50 border-orange-500/30 text-white placeholder:text-slate-500 h-12 text-center text-lg"
            />
            <Button 
              onClick={handleLogin} 
              className="w-full h-12 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-white font-bold text-lg shadow-lg shadow-orange-500/30"
            >
              <Flame className="w-5 h-5 mr-2" />
              IGNITE
            </Button>
          </div>
          
          <div className="mt-8 pt-6 border-t border-slate-700 text-center">
            <p className="text-slate-500 text-sm mb-2">Powered by</p>
            <p className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
              LIONHEART DIGITAL
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Helmet>
        <title>SEO Furnace | Dashboard</title>
      </Helmet>

      {/* Header */}
      <header className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 py-4 px-6 shadow-lg shadow-orange-500/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <Flame className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black">SEO FURNACE</h1>
                <p className="text-xs text-white/70">Incinerate the Competition</p>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={() => { resetWizard(); setShowWizard(true); }}
            className="bg-white text-orange-600 hover:bg-orange-50 font-bold shadow-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Campaign
          </Button>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-slate-800/50 border-b border-slate-700 py-6 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl p-5 border border-orange-500/20">
            <div className="flex items-center gap-2 text-orange-400 text-sm mb-2">
              <Layers className="w-5 h-5" />
              Active Campaigns
            </div>
            <div className="text-4xl font-black text-white">{stats.total_campaigns}</div>
          </div>
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-5 border border-blue-500/20">
            <div className="flex items-center gap-2 text-blue-400 text-sm mb-2">
              <FileText className="w-5 h-5" />
              Pages Generated
            </div>
            <div className="text-4xl font-black text-white">{stats.total_pages}</div>
          </div>
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl p-5 border border-green-500/20">
            <div className="flex items-center gap-2 text-green-400 text-sm mb-2">
              <CheckCircle className="w-5 h-5" />
              Indexed by Google
            </div>
            <div className="text-4xl font-black text-green-400">{stats.indexed_pages}</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-5 border border-purple-500/20">
            <div className="flex items-center gap-2 text-purple-400 text-sm mb-2">
              <Target className="w-5 h-5" />
              Leads Captured
            </div>
            <div className="text-4xl font-black text-purple-400">{stats.total_leads}</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => { resetWizard(); setShowWizard(true); }}
            className="group bg-gradient-to-br from-orange-500/20 to-red-500/20 hover:from-orange-500/30 hover:to-red-500/30 rounded-2xl p-6 border border-orange-500/30 text-left transition-all"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Rocket className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Launch Campaign</h3>
            <p className="text-slate-400 text-sm">Create a new promotional campaign with our step-by-step wizard</p>
          </button>
          
          <button
            onClick={() => setActiveTab('campaigns')}
            className="group bg-gradient-to-br from-blue-500/20 to-cyan-500/20 hover:from-blue-500/30 hover:to-cyan-500/30 rounded-2xl p-6 border border-blue-500/30 text-left transition-all"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <BarChart3 className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">View Campaigns</h3>
            <p className="text-slate-400 text-sm">Monitor and manage your active SEO campaigns</p>
          </button>
          
          <button
            onClick={() => { fetchLeads(); setShowLeads(true); setSelectedCampaign(null); }}
            className="group bg-gradient-to-br from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 rounded-2xl p-6 border border-purple-500/30 text-left transition-all"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Download className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">View & Export Leads</h3>
            <p className="text-slate-400 text-sm">View captured leads and download as CSV</p>
          </button>
        </div>

        {/* Campaigns List */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              Your Campaigns
            </h2>
            <Button variant="outline" size="sm" onClick={fetchCampaigns} className="border-slate-600">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {loading ? (
            <div className="text-center py-12 text-slate-400">
              <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-orange-500" />
              Loading campaigns...
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Flame className="w-10 h-10 text-slate-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">No campaigns yet</h3>
              <p className="text-slate-400 mb-6">Create your first campaign to start generating traffic</p>
              <Button onClick={() => { resetWizard(); setShowWizard(true); }} className="bg-gradient-to-r from-orange-500 to-red-500">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Campaign
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="p-6 hover:bg-slate-800/50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-bold">{campaign.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[campaign.status]}`}>
                          {campaign.status}
                        </span>
                        {campaign.status === 'deployed' && (
                          <a 
                            href={`https://${campaign.domain}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Live
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          {campaign.domain}
                        </span>
                        {campaign.destination_url && (
                          <span className="flex items-center gap-1">
                            <ExternalLink className="w-4 h-4" />
                            {campaign.destination_url}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => viewCampaignDetails(campaign)}
                        className="bg-blue-600 hover:bg-blue-500"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => deployCampaign(campaign.id)}
                        disabled={deploying || campaign.status === 'deploying'}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
                      >
                        <Rocket className="w-4 h-4 mr-1" />
                        {deploying ? 'Deploying...' : 'Deploy'}
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => viewLeads(campaign)}
                        className="bg-purple-600 hover:bg-purple-500"
                      >
                        <Target className="w-4 h-4 mr-1" />
                        Leads
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-orange-400">{campaign.pages_generated || 0}</div>
                      <div className="text-xs text-slate-500">Pages</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-green-400">{campaign.pages_indexed || 0}</div>
                      <div className="text-xs text-slate-500">Indexed</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-blue-400">{campaign.keywords?.length || 0}</div>
                      <div className="text-xs text-slate-500">Keywords</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-black text-purple-400">{campaign.leads || 0}</div>
                      <div className="text-xs text-slate-500">Leads</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Campaign Creation Wizard */}
      {showWizard && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-slate-800 rounded-3xl w-full max-w-3xl border border-slate-700 shadow-2xl my-8">
            {/* Wizard Header */}
            <div className="px-8 py-6 border-b border-slate-700">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Launch Campaign</h2>
                    <p className="text-slate-400 text-sm">Step {wizardStep} of 6</p>
                  </div>
                </div>
                <button onClick={() => setShowWizard(false)} className="text-slate-400 hover:text-white text-2xl">
                  ×
                </button>
              </div>
              
              {/* Progress Steps */}
              <div className="flex items-center justify-between">
                {WIZARD_STEPS.map((step, idx) => {
                  const StepIcon = step.icon;
                  const isActive = wizardStep === step.id;
                  const isComplete = wizardStep > step.id;
                  
                  return (
                    <React.Fragment key={step.id}>
                      <div className={`flex flex-col items-center ${isActive ? 'text-orange-400' : isComplete ? 'text-green-400' : 'text-slate-600'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                          isActive ? 'bg-orange-500/20 border-2 border-orange-500' : 
                          isComplete ? 'bg-green-500/20 border-2 border-green-500' : 
                          'bg-slate-700 border-2 border-slate-600'
                        }`}>
                          {isComplete ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                        </div>
                        <span className="text-xs hidden sm:block">{step.title}</span>
                      </div>
                      {idx < WIZARD_STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-2 ${wizardStep > step.id ? 'bg-green-500' : 'bg-slate-700'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            
            {/* Wizard Content */}
            <div className="p-8">
              {/* Step 1: Domain Setup */}
              {wizardStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Globe className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Domain Setup</h3>
                    <p className="text-slate-400">Configure the domain for your SEO campaign</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Campaign Name</label>
                    <Input
                      placeholder="e.g., Sydney Plumbers Campaign"
                      value={campaignData.name}
                      onChange={(e) => setCampaignData({...campaignData, name: e.target.value})}
                      className="bg-slate-900 border-slate-600 h-12"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Do you have a domain for this campaign?</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setCampaignData({...campaignData, hasDomain: 'yes'})}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          campaignData.hasDomain === 'yes' 
                            ? 'border-orange-500 bg-orange-500/10' 
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        <CheckCircle className={`w-6 h-6 mb-2 ${campaignData.hasDomain === 'yes' ? 'text-orange-400' : 'text-slate-500'}`} />
                        <div className="font-semibold">Yes, I have a domain</div>
                        <div className="text-sm text-slate-400">I'll enter my existing domain</div>
                      </button>
                      <button
                        onClick={() => setCampaignData({...campaignData, hasDomain: 'no'})}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          campaignData.hasDomain === 'no' 
                            ? 'border-orange-500 bg-orange-500/10' 
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        <Plus className={`w-6 h-6 mb-2 ${campaignData.hasDomain === 'no' ? 'text-orange-400' : 'text-slate-500'}`} />
                        <div className="font-semibold">No, I need one</div>
                        <div className="text-sm text-slate-400">Help me set up a domain</div>
                      </button>
                    </div>
                  </div>
                  
                  {campaignData.hasDomain === 'yes' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Your Domain</label>
                      <Input
                        placeholder="e.g., sydneyplumbers.com.au"
                        value={campaignData.domain}
                        onChange={(e) => setCampaignData({...campaignData, domain: e.target.value})}
                        className="bg-slate-900 border-slate-600 h-12"
                      />
                    </div>
                  )}
                  
                  {campaignData.hasDomain === 'no' && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                      <h4 className="font-semibold text-blue-400 mb-2">Domain Registration</h4>
                      <p className="text-slate-300 text-sm mb-3">
                        We recommend registering a domain through Namecheap or GoDaddy. 
                        Once registered, enter it above and we'll help you configure it.
                      </p>
                      <a 
                        href="https://www.namecheap.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
                      >
                        Register at Namecheap <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Destination */}
              {wizardStep === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Link2 className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Destination Website</h3>
                    <p className="text-slate-400">Where should SEO Furnace drive traffic to?</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Destination URL</label>
                    <Input
                      placeholder="e.g., https://www.yourbusiness.com.au"
                      value={campaignData.destinationUrl}
                      onChange={(e) => setCampaignData({...campaignData, destinationUrl: e.target.value})}
                      className="bg-slate-900 border-slate-600 h-12"
                    />
                    <p className="text-slate-500 text-sm mt-2">
                      This is the website visitors will be directed to when they click through from your doorway pages
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3: Website Scan - Mirror Page Branding */}
              {wizardStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <Scan className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Mirror Page Branding</h3>
                    <p className="text-slate-400">Extract branding from the destination site to create matching mirror pages</p>
                  </div>
                  
                  {/* Scan Button */}
                  <div className="text-center py-4">
                    <Button 
                      onClick={scanWebsite}
                      disabled={scanning}
                      className="bg-gradient-to-r from-orange-500 to-red-500 h-12 px-8"
                    >
                      {scanning ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Scanning {campaignData.destinationUrl}...
                        </>
                      ) : (
                        <>
                          <Scan className="w-5 h-5 mr-2" />
                          {scannedData ? 'Re-scan Website' : 'Scan Website'}
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Scanned Data Display */}
                  {scannedData && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
                      <div className="flex items-center gap-2 text-green-400 mb-2">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">Branding extracted! Review and edit below.</span>
                      </div>
                    </div>
                  )}

                  {/* Branding Preview Card */}
                  {(scannedData || campaignData.businessName) && (
                    <div 
                      className="rounded-xl overflow-hidden border border-slate-600"
                      style={{ backgroundColor: campaignData.brandColors?.primary || '#00B0B0' }}
                    >
                      <div className="p-6 text-white text-center">
                        <p className="text-sm opacity-75 mb-2">Mirror Page Preview</p>
                        <h4 className="text-2xl font-light">{campaignData.heroTitle || campaignData.businessName || 'Your Business'}</h4>
                        <p className="opacity-90 mt-2">{campaignData.heroSubtitle || campaignData.businessDescription || 'Your tagline here'}</p>
                        {campaignData.phone && (
                          <p className="text-lg font-semibold mt-3">{campaignData.phone}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Editable Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Business Name</label>
                      <Input
                        placeholder="e.g., Taylors Property"
                        value={campaignData.businessName}
                        onChange={(e) => setCampaignData({...campaignData, businessName: e.target.value})}
                        className="bg-slate-900 border-slate-600 h-11"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
                      <Input
                        placeholder="e.g., 07 5572 6277"
                        value={campaignData.phone || ''}
                        onChange={(e) => setCampaignData({...campaignData, phone: e.target.value})}
                        className="bg-slate-900 border-slate-600 h-11"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Hero Title</label>
                    <Input
                      placeholder="e.g., Your property SOLD with Free Advertising"
                      value={campaignData.heroTitle || ''}
                      onChange={(e) => setCampaignData({...campaignData, heroTitle: e.target.value})}
                      className="bg-slate-900 border-slate-600 h-11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Hero Subtitle / Description</label>
                    <textarea
                      placeholder="Brief description shown on the homepage..."
                      value={campaignData.businessDescription}
                      onChange={(e) => setCampaignData({...campaignData, businessDescription: e.target.value, heroSubtitle: e.target.value})}
                      className="w-full h-20 px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    />
                  </div>

                  {/* Brand Color Picker */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">Brand Color</label>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={campaignData.brandColors?.primary || '#00B0B0'}
                          onChange={(e) => setCampaignData({
                            ...campaignData, 
                            brandColors: { ...campaignData.brandColors, primary: e.target.value }
                          })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-600"
                        />
                        <div>
                          <p className="text-sm text-slate-400">Primary</p>
                          <p className="text-xs text-slate-500 font-mono">{campaignData.brandColors?.primary || '#00B0B0'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={campaignData.brandColors?.secondary || '#008A8A'}
                          onChange={(e) => setCampaignData({
                            ...campaignData, 
                            brandColors: { ...campaignData.brandColors, secondary: e.target.value }
                          })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-600"
                        />
                        <div>
                          <p className="text-sm text-slate-400">Secondary</p>
                          <p className="text-xs text-slate-500 font-mono">{campaignData.brandColors?.secondary || '#008A8A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Navigation Items (if scanned) */}
                  {scannedData?.navItems && scannedData.navItems.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Extracted Navigation Links</label>
                      <div className="flex flex-wrap gap-2">
                        {scannedData.navItems.slice(0, 8).map((item, idx) => (
                          <span key={idx} className="px-3 py-1 bg-slate-700 rounded-full text-sm text-slate-300">
                            {item.text}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Targeting */}
              {wizardStep === 4 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Target className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Geographic Targeting</h3>
                    <p className="text-slate-400">Where should SEO Furnace target traffic?</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">Targeting Scope</label>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { id: 'local', label: 'Local', desc: 'Specific suburbs/cities', icon: MapPin },
                        { id: 'state', label: 'State-wide', desc: 'Entire state coverage', icon: Map },
                        { id: 'national', label: 'National', desc: 'All of Australia', icon: Globe },
                      ].map(({ id, label, desc, icon: Icon }) => (
                        <button
                          key={id}
                          onClick={() => setCampaignData({...campaignData, targetingScope: id})}
                          className={`p-4 rounded-xl border-2 text-center transition-all ${
                            campaignData.targetingScope === id 
                              ? 'border-orange-500 bg-orange-500/10' 
                              : 'border-slate-600 hover:border-slate-500'
                          }`}
                        >
                          <Icon className={`w-8 h-8 mx-auto mb-2 ${campaignData.targetingScope === id ? 'text-orange-400' : 'text-slate-500'}`} />
                          <div className="font-semibold">{label}</div>
                          <div className="text-xs text-slate-400">{desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Target Locations (one per line)
                    </label>
                    <textarea
                      placeholder="Sydney&#10;Melbourne&#10;Brisbane&#10;Gold Coast&#10;Surfers Paradise&#10;..."
                      value={campaignData.locations}
                      onChange={(e) => setCampaignData({...campaignData, locations: e.target.value})}
                      className="w-full h-32 px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Step 5: Products/Services */}
              {wizardStep === 5 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Tag className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Products & Services</h3>
                    <p className="text-slate-400">What keywords and services should we target?</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Services/Products (one per line)
                    </label>
                    <textarea
                      placeholder="Emergency Plumbing&#10;Blocked Drains&#10;Hot Water Systems&#10;Bathroom Renovations&#10;..."
                      value={campaignData.services}
                      onChange={(e) => setCampaignData({...campaignData, services: e.target.value})}
                      className="w-full h-28 px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Keywords - Short & Long Tail (one per line)
                    </label>
                    <textarea
                      placeholder="emergency plumber&#10;24 hour plumber near me&#10;blocked drain repair service&#10;hot water system installation&#10;..."
                      value={campaignData.keywords}
                      onChange={(e) => setCampaignData({...campaignData, keywords: e.target.value})}
                      className="w-full h-32 px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                    />
                    <p className="text-slate-500 text-sm mt-2">
                      Pages will be created: domain.com/[location]/[keyword]
                    </p>
                  </div>
                </div>
              )}

              {/* Step 6: Launch */}
              {wizardStep === 6 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Rocket className="w-16 h-16 mx-auto text-orange-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">Ready to Launch! 🔥</h3>
                    <p className="text-slate-400">Review your campaign and prepare for liftoff</p>
                  </div>
                  
                  {/* Campaign Summary */}
                  <div className="bg-slate-900/50 rounded-xl p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-slate-500 text-sm">Campaign Name</span>
                        <p className="font-semibold">{campaignData.name || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Domain</span>
                        <p className="font-semibold">{campaignData.domain || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Destination</span>
                        <p className="font-semibold truncate">{campaignData.destinationUrl || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Targeting</span>
                        <p className="font-semibold capitalize">{campaignData.targetingScope}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Locations</span>
                        <p className="font-semibold">{campaignData.locations.split('\n').filter(l => l.trim()).length} locations</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-sm">Keywords</span>
                        <p className="font-semibold">{campaignData.keywords.split('\n').filter(k => k.trim()).length} keywords</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Generation Settings */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Pages to Generate: <span className="text-orange-400 font-bold">{campaignData.pagesToGenerate}</span>
                    </label>
                    <input
                      type="range"
                      min="25"
                      max="500"
                      step="25"
                      value={campaignData.pagesToGenerate}
                      onChange={(e) => setCampaignData({...campaignData, pagesToGenerate: parseInt(e.target.value)})}
                      className="w-full accent-orange-500"
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>25 pages</span>
                      <span>500 pages</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        checked={campaignData.includeAiContent}
                        onChange={(e) => setCampaignData({...campaignData, includeAiContent: e.target.checked})}
                        className="rounded border-slate-600 text-orange-500 focus:ring-orange-500"
                      />
                      <div>
                        <div className="font-medium flex items-center gap-1">
                          <Sparkles className="w-4 h-4 text-orange-400" />
                          AI Content
                        </div>
                        <div className="text-xs text-slate-400">Generate unique content with AI</div>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        checked={campaignData.generateSitemap}
                        onChange={(e) => setCampaignData({...campaignData, generateSitemap: e.target.checked})}
                        className="rounded border-slate-600 text-orange-500 focus:ring-orange-500"
                      />
                      <div>
                        <div className="font-medium flex items-center gap-1">
                          <FileText className="w-4 h-4 text-blue-400" />
                          Sitemap
                        </div>
                        <div className="text-xs text-slate-400">Auto-generate XML sitemap</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
            
            {/* Wizard Footer */}
            <div className="px-8 py-6 border-t border-slate-700 flex items-center justify-between">
              <Button 
                variant="outline" 
                onClick={wizardStep === 1 ? () => setShowWizard(false) : prevStep}
                className="border-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {wizardStep === 1 ? 'Cancel' : 'Back'}
              </Button>
              
              {wizardStep < 6 ? (
                <Button 
                  onClick={nextStep}
                  className="bg-gradient-to-r from-orange-500 to-red-500"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={createCampaign}
                  disabled={loading}
                  className="bg-gradient-to-r from-orange-500 to-red-500 px-8"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Launch Campaign 🔥
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Leads Modal */}
      {showLeads && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-slate-800 rounded-3xl w-full max-w-4xl border border-slate-700 shadow-2xl my-8">
            {/* Leads Header */}
            <div className="px-8 py-6 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">
                  {selectedCampaign ? `Leads: ${selectedCampaign.name}` : 'All Leads'}
                </h2>
                <p className="text-slate-400 text-sm">{leads.length} leads captured</p>
              </div>
              <div className="flex gap-3">
                <Button onClick={exportLeadsCSV} className="bg-green-600 hover:bg-green-500">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <button onClick={() => setShowLeads(false)} className="text-slate-400 hover:text-white text-2xl">
                  ×
                </button>
              </div>
            </div>
            
            {/* Leads List */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {leads.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No leads captured yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {leads.map((lead, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-xl p-5 border border-slate-700">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-bold text-lg text-white">{lead.name}</h4>
                          <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                            <span>{lead.email}</span>
                            {lead.phone && <span>{lead.phone}</span>}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          lead.status === 'new' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                          lead.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                          lead.status === 'qualified' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                          'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                        }`}>
                          {lead.status}
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm mb-3">{lead.message}</p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Source: {lead.source_page}</span>
                        <span>{new Date(lead.created_at).toLocaleDateString('en-AU', { 
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Campaign Detail Modal */}
      {showCampaignDetail && selectedCampaign && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-slate-800 rounded-3xl w-full max-w-4xl border border-slate-700 shadow-2xl my-8">
            {/* Header */}
            <div className="px-8 py-6 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <Flame className="w-6 h-6 text-orange-500" />
                  {selectedCampaign.name}
                </h2>
                <p className="text-slate-400 text-sm mt-1">Campaign Details & Analytics</p>
              </div>
              <button onClick={() => setShowCampaignDetail(false)} className="text-slate-400 hover:text-white text-2xl">
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="p-8 space-y-6">
              {/* Status & Links */}
              <div className="flex flex-wrap items-center gap-4">
                <span className={`px-4 py-2 rounded-full text-sm font-medium border ${STATUS_COLORS[selectedCampaign.status]}`}>
                  {selectedCampaign.status?.toUpperCase()}
                </span>
                {selectedCampaign.domain && (
                  <a 
                    href={`https://${selectedCampaign.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
                  >
                    <Globe className="w-4 h-4" />
                    {selectedCampaign.domain}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {selectedCampaign.sitemap_url && (
                  <a 
                    href={selectedCampaign.sitemap_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-400 hover:text-green-300"
                  >
                    <FileText className="w-4 h-4" />
                    Sitemap
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-900/50 rounded-xl p-6 text-center border border-slate-700">
                  <div className="text-4xl font-black text-orange-400">{selectedCampaign.pages_generated || 0}</div>
                  <div className="text-sm text-slate-400 mt-1">Pages Generated</div>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-6 text-center border border-slate-700">
                  <div className="text-4xl font-black text-green-400">{selectedCampaign.pages_indexed || 0}</div>
                  <div className="text-sm text-slate-400 mt-1">Indexed by Google</div>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-6 text-center border border-slate-700">
                  <div className="text-4xl font-black text-blue-400">{selectedCampaign.keywords?.length || 0}</div>
                  <div className="text-sm text-slate-400 mt-1">Keywords</div>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-6 text-center border border-slate-700">
                  <div className="text-4xl font-black text-purple-400">{selectedCampaign.leads || 0}</div>
                  <div className="text-sm text-slate-400 mt-1">Leads Captured</div>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-6">
                {/* Destination */}
                <div className="bg-slate-900/30 rounded-xl p-5 border border-slate-700">
                  <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-blue-400" />
                    Destination
                  </h4>
                  <p className="text-slate-300">{selectedCampaign.business_name || 'Not set'}</p>
                  {selectedCampaign.destination_url && (
                    <a 
                      href={selectedCampaign.destination_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 text-sm hover:underline flex items-center gap-1 mt-2"
                    >
                      {selectedCampaign.destination_url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>

                {/* Contact */}
                <div className="bg-slate-900/30 rounded-xl p-5 border border-slate-700">
                  <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-400" />
                    Contact
                  </h4>
                  <p className="text-slate-300">{selectedCampaign.phone || 'Not set'}</p>
                </div>
              </div>

              {/* Locations */}
              {selectedCampaign.locations?.length > 0 && (
                <div className="bg-slate-900/30 rounded-xl p-5 border border-slate-700">
                  <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-400" />
                    Target Locations ({selectedCampaign.locations.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCampaign.locations.map((loc, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-300 border border-slate-600">
                        {loc}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Keywords */}
              {selectedCampaign.keywords?.length > 0 && (
                <div className="bg-slate-900/30 rounded-xl p-5 border border-slate-700">
                  <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-yellow-400" />
                    Target Keywords ({selectedCampaign.keywords.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCampaign.keywords.map((kw, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-300 border border-slate-600">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Services */}
              {selectedCampaign.services?.length > 0 && (
                <div className="bg-slate-900/30 rounded-xl p-5 border border-slate-700">
                  <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-cyan-400" />
                    Services ({selectedCampaign.services.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCampaign.services.map((svc, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-300 border border-slate-600">
                        {svc}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <Button 
                  onClick={() => { viewLeads(selectedCampaign); setShowCampaignDetail(false); }}
                  className="bg-purple-600 hover:bg-purple-500 flex-1"
                >
                  <Target className="w-4 h-4 mr-2" />
                  View Leads ({selectedCampaign.leads || 0})
                </Button>
                <Button 
                  onClick={() => { deployCampaign(selectedCampaign.id); setShowCampaignDetail(false); }}
                  disabled={deploying}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 flex-1"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  {deploying ? 'Deploying...' : 'Deploy Campaign'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
              SEO FURNACE
            </span>
          </div>
          <p className="text-slate-500 text-sm mb-2">Powered by</p>
          <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-yellow-500">
            LIONHEART DIGITAL
          </p>
          <p className="text-slate-600 text-sm italic mt-2">"They lit the fire. We became it."</p>
        </div>
      </footer>
    </div>
  );
};

export default SEOFurnace;
